import React, { useState } from 'react';
import { Search, ChevronRight, AlertTriangle, Check, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function RegulationsScreen() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const regulations = [{
    id: '1',
    region: 'North Lake District',
    status: 'open',
    rules: ['Catch and release for Trout', 'No live bait', 'Barbless hooks only'],
    season: 'Apr 1 - Oct 31',
    locations: ['Emerald Lake', 'Twin Peaks Pond']
  }, {
    id: '2',
    region: 'Coastal Zone',
    status: 'restricted',
    rules: ['Daily limit: 2 Salmon', 'Record catch immediately', 'Closed Tuesdays'],
    season: 'Year round',
    locations: ['Misty Coast', 'Sunset Cove']
  }, {
    id: '3',
    region: 'Mountain Streams',
    status: 'closed',
    rules: ['Spawning season closure', 'No fishing allowed'],
    season: 'Opens June 15',
    locations: ['Crystal Creek']
  }];
  const filtered = regulations.filter(r => r.region.toLowerCase().includes(searchQuery.toLowerCase()));
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400';
      case 'restricted':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400';
      case 'closed':
        return 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  return <div className="p-4 md:p-8 max-w-2xl mx-auto pb-24 md:pb-8" data-id="element-270">
      <header className="mb-6" data-id="element-271">
        <h1 className="text-2xl font-semibold text-[var(--text-primary)]" data-id="element-272">
          Regulations
        </h1>
        <p className="text-[var(--text-secondary)] text-sm mt-1" data-id="element-273">
          Local fishing rules and seasons
        </p>
      </header>

      {/* Search */}
      <div className="relative mb-6" data-id="element-274">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" data-id="element-275" />
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search regions..." className="w-full pl-10 pr-4 py-3 rounded-xl input-minimal text-[var(--text-primary)]" data-id="element-276" />
      </div>

      {/* List */}
      <div className="space-y-3" data-id="element-277">
        {filtered.map((reg, i) => <motion.div key={reg.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.05
      }} className="card overflow-hidden" data-id="element-278">
            <button onClick={() => setExpandedId(expandedId === reg.id ? null : reg.id)} className="w-full p-4 text-left hover:bg-[var(--bg-secondary)] transition-colors" data-id="element-279">
              <div className="flex items-start justify-between" data-id="element-280">
                <div data-id="element-281">
                  <h3 className="font-medium text-[var(--text-primary)]" data-id="element-282">
                    {reg.region}
                  </h3>
                  <p className="text-sm text-[var(--text-muted)] mt-0.5" data-id="element-283">
                    {reg.season}
                  </p>
                </div>
                <div className="flex items-center space-x-2" data-id="element-284">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusStyle(reg.status)}`} data-id="element-285">
                    {reg.status}
                  </span>
                  <ChevronRight className={`w-4 h-4 text-[var(--text-muted)] transition-transform ${expandedId === reg.id ? 'rotate-90' : ''}`} data-id="element-286" />
                </div>
              </div>
            </button>

            <AnimatePresence data-id="element-287">
              {expandedId === reg.id && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="border-t border-[var(--border-subtle)] bg-[var(--bg-secondary)]" data-id="element-288">
                  <div className="p-4 space-y-3" data-id="element-289">
                    <div data-id="element-290">
                      <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide mb-2" data-id="element-291">
                        Rules
                      </p>
                      <ul className="space-y-1.5" data-id="element-292">
                        {reg.rules.map((rule, j) => <li key={j} className="flex items-start space-x-2 text-sm text-[var(--text-secondary)]" data-id="element-293">
                            {reg.status === 'closed' ? <AlertTriangle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" data-id="element-294" /> : <Check className="w-4 h-4 text-[var(--accent)] mt-0.5 flex-shrink-0" data-id="element-295" />}
                            <span data-id="element-296">{rule}</span>
                          </li>)}
                      </ul>
                    </div>
                    <div data-id="element-297">
                      <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide mb-2" data-id="element-298">
                        Locations
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-299">
                        {reg.locations.map(loc => <span key={loc} className="chip px-2.5 py-1 flex items-center" data-id="element-300">
                            <MapPin className="w-3 h-3 mr-1" data-id="element-301" />
                            {loc}
                          </span>)}
                      </div>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </motion.div>)}
      </div>
    </div>;
}